import React from 'react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Newspaper, Award, ExternalLink, FileText } from 'lucide-react';
import SEO from '../components/SEO';

const Press = () => {
  const schema = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "ProfilePage",
        "name": "Laura Novak - Journalism Career",
        "url": "https://lauranovakwrites.com/news",
        "about": {
          "@type": "Person",
          "name": "Laura Novak",
          "jobTitle": "Journalist and Author"
        }
      },
      {
        "@type": "BreadcrumbList",
        "itemListElement": [
          {
            "@type": "ListItem",
            "position": 1,
            "name": "Home",
            "item": "https://lauranovakwrites.com"
          },
          {
            "@type": "ListItem",
            "position": 2,
            "name": "News",
            "item": "https://lauranovakwrites.com/news"
          }
        ]
      }
    ]
  };

  const pressItems = [
    {
      title: 'The New York Times',
      description: 'Ten years reporting on Health, Business, and the Arts',
      topics: ['Health', 'Business', 'Arts', 'Non-Profits', 'Pediatric Medicine'],
      icon: Newspaper
    },
    {
      title: 'Broadcast Journalism',
      description: 'ABC News (London & New York), CBS Radio San Francisco',
      topics: ['Crime Reporting', 'Breaking News', 'Features', 'Investigations'],
      icon: FileText
    },
    {
      title: 'Notable Coverage',
      description: 'In-depth features in national magazines and newspapers',
      topics: ['Art Conservation', 'Culture', 'Finance', 'Food'],
      icon: Award
    }
  ];

  return (
    <div className="min-h-screen">
      <SEO 
        title="News & Press - Laura Novak's Journalism Career"
        description="Laura Novak's distinguished journalism career: 25+ years with The New York Times, ABC News, and more. Award-winning reporting on health, business, arts, and culture."
        keywords="Laura Novak journalism, NYT reporter, ABC News, broadcast journalist, health reporter, business journalism, arts coverage"
        url="https://lauranovakwrites.com/news"
        schema={schema}
      />
      
      {/* Hero Section */}
      <section className="relative py-32 bg-slate-900">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <Newspaper className="w-16 h-16 text-amber-400 mx-auto mb-6" />
          <h1 className="text-5xl md:text-7xl font-serif font-bold text-white mb-6">
            Press & Articles
          </h1>
          <p className="text-xl md:text-2xl text-slate-300">
            25+ years of gumshoe journalism
          </p>
        </div>
      </section>

      {/* Press Highlights */}
      <section className="py-20 bg-white">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-serif font-bold text-slate-900 mb-4">
              Journalism Career Highlights
            </h2>
            <p className="text-xl text-slate-600 max-w-3xl mx-auto">
              From breaking news to in-depth features, my work has appeared in prestigious publications and broadcast networks
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 mb-20">
            {pressItems.map((item) => {
              const Icon = item.icon;
              return (
                <Card key={item.title} className="border-2 hover:border-amber-500 transition-all duration-300 hover:shadow-xl">
                  <CardContent className="p-8">
                    <div className="bg-amber-100 p-4 rounded-lg w-fit mb-6">
                      <Icon className="w-8 h-8 text-amber-600" />
                    </div>
                    <h3 className="text-2xl font-bold text-slate-900 mb-3">{item.title}</h3>
                    <p className="text-slate-600 mb-6 leading-relaxed">{item.description}</p>
                    <div className="flex flex-wrap gap-2">
                      {item.topics.map((topic) => (
                        <span key={topic} className="bg-slate-100 text-slate-700 px-3 py-1 rounded-full text-sm">
                          {topic}
                        </span>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>

          {/* Featured Articles */}
          <div className="bg-slate-50 rounded-2xl p-12">
            <h2 className="text-3xl font-serif font-bold text-slate-900 mb-8 text-center">
              Notable Published Works
            </h2>
            <div className="space-y-6 max-w-4xl mx-auto">
              <Card className="hover:shadow-lg transition-shadow">
                <CardContent className="p-6">
                  <div className="flex justify-between items-start">
                    <div>
                      <h4 className="text-xl font-bold text-slate-900 mb-2">The New York Times</h4>
                      <p className="text-slate-600 mb-3">Ten years covering Health, Business, and the Arts in California</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="hover:shadow-lg transition-shadow">
                <CardContent className="p-6">
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <h4 className="text-xl font-bold text-slate-900 mb-2">ABC News – London and New York</h4>
                      <p className="text-slate-600 mb-3">David Jayne Fellow and reporter for Satellite News Channel</p>
                      <div className="flex flex-wrap gap-2">
                        <span className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm font-semibold">Nightline</span>
                        <span className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm font-semibold">World News Tonight</span>
                        <span className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm font-semibold">Weekend News</span>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="hover:shadow-lg transition-shadow">
                <CardContent className="p-6">
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <h4 className="text-xl font-bold text-slate-900 mb-2">CBS Radio SF & KFBK Sacramento</h4>
                      <p className="text-slate-600 mb-3">Writer, editor, anchor and general assignment reporter</p>
                      <div className="flex flex-wrap gap-2">
                        <span className="bg-red-100 text-red-800 px-3 py-1 rounded-full text-sm font-semibold">Breaking News</span>
                        <span className="bg-red-100 text-red-800 px-3 py-1 rounded-full text-sm font-semibold">Features</span>
                        <span className="bg-red-100 text-red-800 px-3 py-1 rounded-full text-sm font-semibold">Crime</span>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="hover:shadow-lg transition-shadow">
                <CardContent className="p-6">
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <h4 className="text-xl font-bold text-slate-900 mb-2">Television: CBS, NBC and Fox (SF, Oakland, San Jose)</h4>
                      <p className="text-slate-600 mb-3">On-camera reporter covering crime and community stories</p>
                      <div className="flex flex-wrap gap-2">
                        <span className="bg-purple-100 text-purple-800 px-3 py-1 rounded-full text-sm font-semibold">Television</span>
                        <span className="bg-purple-100 text-purple-800 px-3 py-1 rounded-full text-sm font-semibold">Crime Coverage</span>
                        <span className="bg-purple-100 text-purple-800 px-3 py-1 rounded-full text-sm font-semibold">Features</span>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Credentials */}
      <section className="py-20 bg-slate-900 text-white">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center mb-16">
            <Award className="w-12 h-12 text-amber-400 mx-auto mb-4" />
            <h2 className="text-4xl font-serif font-bold mb-4">Education & Recognition</h2>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            <Card className="bg-slate-800 border-slate-700">
              <CardContent className="p-8 text-center">
                <h3 className="text-2xl font-bold text-amber-400 mb-3">Barnard College</h3>
                <p className="text-slate-300 mb-2">Columbia University</p>
                <p className="text-slate-400 text-sm">BA</p>
              </CardContent>
            </Card>
            <Card className="bg-slate-800 border-slate-700">
              <CardContent className="p-8 text-center">
                <h3 className="text-2xl font-bold text-amber-400 mb-3">Columbia University</h3>
                <p className="text-slate-300 mb-2">Graduate School of Journalism</p>
                <p className="text-slate-400 text-sm mb-3">MS</p>
                <div className="pt-3 border-t border-slate-700">
                  <p className="text-amber-300 text-sm font-semibold">David Jayne Fellow</p>
                  <p className="text-slate-400 text-xs">ABC News</p>
                </div>
              </CardContent>
            </Card>
            <Card className="bg-slate-800 border-slate-700">
              <CardContent className="p-8 text-center">
                <h3 className="text-2xl font-bold text-amber-400 mb-3">Leningrad State University</h3>
                <p className="text-slate-300 mb-2">USSR</p>
                <p className="text-slate-400 text-sm">Diploma with Honors</p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Decorative Separator before Footer */}
      <div className="h-2 bg-gradient-to-r from-transparent via-amber-500 to-transparent opacity-60"></div>
    </div>
  );
};

export default Press;